import { VStack } from '@chakra-ui/react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { ErrorState, LoadingState } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../api/hotelsApi'
import { ExtensionActivationList } from '../components/settings/ExtensionActivationList'
import { ResetScheduleForm } from '../components/settings/ResetScheduleForm'
import { HotelsI18nProvider, useHotelsI18n } from '../i18n'
import type { HotelsTranslationLike } from '../i18n'
import { useGetPbx } from '../queries/hooks'
import type { HospitalityPermissions } from '../types/rooms'

interface HotelsSettingsPageProps {
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  toast: ToastLike
  permissions: HospitalityPermissions
  showActivation?: boolean
  t?: HotelsTranslationLike
  locale?: string
}

/**
 * Hotels Settings page.
 * Layout matches legacy:
 *   1. Reset Rooms to Dirty — radio (Everyday @ time / Manually)
 *   2. Activation — table of all extensions with hospitality toggle
 */
export const HotelsSettingsPage = ({
  t,
  locale,
  ...props
}: HotelsSettingsPageProps) => {
  return (
    <HotelsI18nProvider t={t} locale={locale}>
      <HotelsSettingsPageInner {...props} />
    </HotelsI18nProvider>
  )
}

const HotelsSettingsPageInner = ({
  api,
  pbxId,
  pbxUuid,
  toast,
  permissions,
  showActivation = true,
}: HotelsSettingsPageProps) => {
  const { text } = useHotelsI18n()
  const pbxQuery = useGetPbx({ api, pbxUuid, pbxId })

  if (pbxQuery.isLoading)
    return (
      <LoadingState
        title=""
        size="lg"
        ariaLabel={text('hotels.common.loading', 'Loading')}
      />
    )
  if (pbxQuery.isError) {
    return (
      <ErrorState
        title={text(
          'hotels.errors.loadPbxSettings',
          'Failed to load PBX settings',
        )}
        retryLabel={text('hotels.common.retry', 'Try again')}
        onRetry={() => pbxQuery.refetch()}
      />
    )
  }

  return (
    <VStack align="stretch" gap={8}>
      <ResetScheduleForm
        api={api}
        pbxId={pbxId}
        pbxUuid={pbxUuid}
        toast={toast}
        currentResetTime={pbxQuery.data?.xml_rebuilt_at}
        canSave={!!permissions.canThumbDownloadSettings}
      />
      {showActivation && (
        <ExtensionActivationList
          api={api}
          pbxId={pbxId}
          toast={toast}
          canEdit={!!permissions.canEditSettings}
        />
      )}
    </VStack>
  )
}
